export function dateLinesFromMatch(match) {
    const t = Number(match?.timestamp);
    if (Number.isFinite(t)) {
        const d = new Date(t < 1e12 ? t * 1000 : t);
        if (!Number.isNaN(d.getTime())) {
            const currentYear = new Date().getFullYear();
            const matchYear = d.getFullYear();
            const dateLine = d
                .toLocaleDateString("en-GB", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    ...(matchYear !== currentYear ? { year: "numeric" } : {}),
                })
                .replace(/,/g, "");
            const timeLine = d.toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            });
            return { dateLine, timeLine };
        }
    }
    return { dateLine: "—", timeLine: "" };
}

export function formatRatingTooltipDate(timestamp) {
    const t = Number(timestamp);
    if (!Number.isFinite(t) || t <= 0) return "—";

    const d = new Date(t < 1e12 ? t * 1000 : t);
    if (Number.isNaN(d.getTime())) return "—";

    const weekday = d.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase();
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleDateString("en-GB", { month: "short" }).toUpperCase();
    const year = d.getFullYear();
    const time = d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });

    return `${weekday}, ${day} ${month} ${year}, ${time}`;
}

export function buildGameAxisTicks(matchCount) {
    if (matchCount <= 0) {
        return [];
    }
    if (matchCount === 1) {
        return [1];
    }

    let step = 30;
    if (matchCount <= 20) {
        step = 5;
    } else if (matchCount <= 60) {
        step = 10;
    }

    const ticks = [1];
    let tick = 1 + step;

    while (tick < matchCount) {
        ticks.push(tick);
        tick += step;
    }

    if (ticks[ticks.length - 1] !== matchCount) {
        ticks.push(matchCount);
    }

    return ticks;
}

export function buildEloProgressionSeries(last25MatchData, maxMatches = 30) {
    const sorted = Object.values(last25MatchData || {})
        .filter(Boolean)
        .sort((a, b) => (Number(a.timestamp) || 0) - (Number(b.timestamp) || 0));

    const matches =
        maxMatches == null
            ? sorted
            : sorted.slice(-maxMatches);

    let lastElo = null;
    const points = matches.map((row, idx) => {
        const eloAfter = Number(row.eloAfter);
        const eloChange = Number(row.eloChange);
        let elo = null;

        if (Number.isFinite(eloAfter) && eloAfter !== 0) {
            elo = Math.round(eloAfter);
        } else if (lastElo != null && Number.isFinite(eloChange)) {
            elo = Math.round(lastElo + eloChange);
        } else if (Number.isFinite(eloAfter)) {
            elo = Math.round(eloAfter);
        }

        if (elo != null) {
            lastElo = elo;
        }

        const ts = Number(row.timestamp);
        const ms = ts < 1e12 ? ts * 1000 : ts;
        const date = new Date(ms);
        const dateLabel = Number.isNaN(date.getTime())
            ? "—"
            : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

        const win = row.outcome === "Win";
        const myScore =
            row.playerInTeam === "team1" ? row.team1Score : row.team2Score;
        const oppScore =
            row.playerInTeam === "team1" ? row.team2Score : row.team1Score;

        return elo != null
            ? {
                idx,
                gameNumber: idx + 1,
                elo,
                dateLabel,
                timestamp: Number(row.timestamp) || 0,
                won: win,
                myScore: Number(myScore) || 0,
                oppScore: Number(oppScore) || 0,
                kills: Number(row.kills) || 0,
                deaths: Number(row.deaths) || 0,
                assists: Number(row.assists) || 0,
                eloChange: Number.isFinite(eloChange) ? Math.round(eloChange) : 0,
            }
            : null;
    }).filter(Boolean);

    return {
        matchCount: matches.length,
        points,
    };
}

export function buildEloProgressionStats(last25MatchData, maxMatches = 30) {
    const sorted = Object.values(last25MatchData || {})
        .filter(Boolean)
        .sort((a, b) => (Number(a.timestamp) || 0) - (Number(b.timestamp) || 0));

    const matches =
        maxMatches == null
            ? sorted
            : sorted.slice(-maxMatches);

    let wins = 0;
    let losses = 0;

    for (const row of matches) {
        if (row.outcome === "Win") {
            wins += 1;
        } else if (row.outcome === "Loss") {
            losses += 1;
        }
    }

    const { points } = buildEloProgressionSeries(last25MatchData, maxMatches);

    if (points.length === 0) {
        return {
            matchCount: matches.length,
            wins,
            losses,
            highestElo: null,
            lowestElo: null,
            eloChange: null,
        };
    }

    const elos = points.map((point) => point.elo);
    const highestElo = Math.max(...elos);
    const lowestElo = Math.min(...elos);
    const eloChange = points[points.length - 1].elo - points[0].elo;

    return {
        matchCount: matches.length,
        wins,
        losses,
        highestElo,
        lowestElo,
        eloChange,
    };
}

export function getHighestWinStreak(last25MatchData, maxMatches = 30) {
    const matches = Object.values(last25MatchData || {})
        .filter(Boolean)
        .sort((a, b) => (Number(a.timestamp) || 0) - (Number(b.timestamp) || 0))
        .slice(-maxMatches);

    let maxStreak = 0;
    let currentStreak = 0;

    for (const row of matches) {
        if (row.outcome === "Win") {
            currentStreak += 1;
            maxStreak = Math.max(maxStreak, currentStreak);
        } else {
            currentStreak = 0;
        }
    }

    return maxStreak;
}

export function buildRating3TrendSeries(last25MatchData, maxMatches = 20) {
    const matches = getMatchesInRange(last25MatchData, maxMatches);

    const points = matches.map((row, idx) => {
        const raw =
            row.rating3 ??
            row.outcomeSummary?.rating3?.matchRating ??
            null;
        const rating = Number(raw);
        if (!Number.isFinite(rating)) {
            return null;
        }

        const win = row.outcome === "Win";
        const myScore =
            row.playerInTeam === "team1" ? row.team1Score : row.team2Score;
        const oppScore =
            row.playerInTeam === "team1" ? row.team2Score : row.team1Score;

        return {
            gameNumber: idx + 1,
            rating: Math.round(rating * 100) / 100,
            won: win,
            myScore: Number(myScore) || 0,
            oppScore: Number(oppScore) || 0,
            timestamp: Number(row.timestamp) || 0,
            matchId: row.matchId,
        };
    }).filter(Boolean);

    return {
        matchCount: matches.length,
        points,
    };
}

export function buildRating3ProgressionStats(points, currentRating) {
    if (!points.length) {
        const current = Number(currentRating);
        return {
            current: Number.isFinite(current) ? current : null,
            peak: null,
            lowest: null,
            avg: null,
        };
    }

    const ratings = points.map((point) => point.rating);
    const peak = Math.max(...ratings);
    const lowest = Math.min(...ratings);
    const avg =
        Math.round(
            (ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length) *
                100,
        ) / 100;
    const resolvedCurrent = Number(currentRating);
    const current = Number.isFinite(resolvedCurrent)
        ? resolvedCurrent
        : points[points.length - 1]?.rating ?? null;

    return {
        current,
        peak,
        lowest,
        avg,
    };
}

export function buildProfileMatches(last25MatchData, limit) {
    let rows = Object.values(last25MatchData || {})
        .filter(Boolean)
        .sort((a, b) => (Number(b.timestamp) || 0) - (Number(a.timestamp) || 0));

    if (Number.isFinite(limit) && limit > 0) {
        rows = rows.slice(0, limit);
    }

    return rows.map((row) => {
        const win = row.outcome === "Win";
        const my =
            row.playerInTeam === "team1" ? row.team1Score : row.team2Score;
        const opp =
            row.playerInTeam === "team1" ? row.team2Score : row.team1Score;
        const eloChange = Number(row.eloChange);
        const eloAfter = Number(row.eloAfter);
        const rawRating =
            row.rating3 ??
            row.outcomeSummary?.rating3?.matchRating ??
            null;
        const rating3 = Number(rawRating);

        return {
            matchId: row.matchId != null ? String(row.matchId) : null,
            map: row.map,
            result: win ? "win" : "loss",
            matchScore: {
                myTeam: Number(my) || 0,
                opponentTeam: Number(opp) || 0,
            },
            kills: Number(row.kills) || 0,
            deaths: Number(row.deaths) || 0,
            assists: Number(row.assists) || 0,
            adr: Number(row.adr) || 0,
            rating3: Number.isFinite(rating3) ? rating3 : null,
            eloChange: Number.isFinite(eloChange) ? eloChange : 0,
            eloAfter:
                Number.isFinite(eloAfter) && eloAfter !== 0
                    ? Math.round(eloAfter)
                    : null,
            timestamp: Number(row.timestamp) || 0,
        };
    });
}

export function getMatchesInRange(last25MatchData, maxMatches = 30) {
    const sorted = Object.values(last25MatchData || {})
        .filter(Boolean)
        .sort((a, b) => (Number(a.timestamp) || 0) - (Number(b.timestamp) || 0));

    return maxMatches == null ? sorted : sorted.slice(-maxMatches);
}

export function buildPerformanceCombatStats(last25MatchData, maxMatches = 30) {
    const matches = getMatchesInRange(last25MatchData, maxMatches);

    if (matches.length === 0) {
        return {
            matchCount: 0,
            avgKills: null,
            avgDeaths: null,
            avgAssists: null,
            kd: null,
            kr: null,
            hsPercent: null,
            adr: null,
        };
    }

    let totalKills = 0;
    let totalDeaths = 0;
    let totalAssists = 0;
    let totalRounds = 0;
    let totalHeadshots = 0;
    let adrSum = 0;
    let adrCount = 0;
    let hasHeadshotData = false;

    for (const row of matches) {
        const kills = Number(row.kills) || 0;
        const deaths = Number(row.deaths) || 0;
        const assists = Number(row.assists) || 0;
        const adr = Number(row.adr);
        const damage = Number(row.damage);

        totalKills += kills;
        totalDeaths += deaths;
        totalAssists += assists;

        if (Number.isFinite(adr) && adr > 0 && Number.isFinite(damage) && damage > 0) {
            totalRounds += damage / adr;
        }

        if (Number.isFinite(adr)) {
            adrSum += adr;
            adrCount += 1;
        }

        const headshotKills = Number(
            row.headshot_kills ?? row.headshotKills ?? row.headshots,
        );
        if (Number.isFinite(headshotKills)) {
            hasHeadshotData = true;
            totalHeadshots += headshotKills;
        }
    }

    const matchCount = matches.length;
    const kd = totalKills / Math.max(totalDeaths, 1);
    const kr = totalRounds > 0 ? totalKills / totalRounds : null;
    const adr = adrCount > 0 ? adrSum / adrCount : null;
    const hsPercent =
        hasHeadshotData && totalKills > 0
            ? (totalHeadshots / totalKills) * 100
            : null;

    return {
        matchCount,
        avgKills: totalKills / matchCount,
        avgDeaths: totalDeaths / matchCount,
        avgAssists: totalAssists / matchCount,
        kd,
        kr,
        hsPercent,
        adr,
    };
}

export function getProfileTotalMatchCount(last25MatchData, matchSummary) {
    if (Number.isFinite(matchSummary?.totalPlayedMatches)) {
        return matchSummary.totalPlayedMatches;
    }

    return Object.values(last25MatchData || {}).filter(Boolean).length;
}

export function shouldUseMatchSummaryForRecord(selectedRange, matchSummary) {
    const totalPlayed = matchSummary?.totalPlayedMatches;
    if (!Number.isFinite(totalPlayed) || totalPlayed <= 0) {
        return false;
    }

    if (selectedRange?.maxMatches == null) {
        return true;
    }

    return totalPlayed <= selectedRange.maxMatches;
}

export function buildRecentProfileMatches(last25MatchData, limit = 5) {
    return buildProfileMatches(last25MatchData, limit);
}

export function buildMapRatingAverages(last25MatchData) {
    const totals = {};

    for (const row of Object.values(last25MatchData || {})) {
        const mapId = row?.map;
        if (!mapId) {
            continue;
        }

        const raw =
            row.rating3 ??
            row.outcomeSummary?.rating3?.matchRating ??
            null;
        const rating = Number(raw);
        if (!Number.isFinite(rating)) {
            continue;
        }

        if (!totals[mapId]) {
            totals[mapId] = { sum: 0, count: 0 };
        }

        totals[mapId].sum += rating;
        totals[mapId].count += 1;
    }

    return Object.fromEntries(
        Object.entries(totals).map(([mapId, { sum, count }]) => [
            mapId,
            Math.round((sum / count) * 100) / 100,
        ]),
    );
}

export function extractMatchKastPercent(row) {
    const kast = Number(row?.kast);
    const roundsPlayed = Number(row?.rounds_played ?? row?.roundsPlayed);

    if (!Number.isFinite(kast)) {
        return null;
    }

    if (kast <= 0) {
        return null;
    }

    // API returns percentage directly after edge normalization.
    if (kast <= 100 && (!Number.isFinite(roundsPlayed) || kast > roundsPlayed)) {
        return Math.round(kast * 10) / 10;
    }

    // Backward compatibility: raw KAST round count / total rounds.
    if (Number.isFinite(roundsPlayed) && roundsPlayed > 0 && kast <= roundsPlayed) {
        return Math.round((kast / roundsPlayed) * 1000) / 10;
    }

    return null;
}

export function buildMapPoolCombatSeries(
    last25MatchData,
    mapsStats,
    limit = null,
    maxMatches = null,
) {
    const mapIds = new Set(
        getSortedMapStatEntries(mapsStats, limit).map(([mapId]) => mapId),
    );
    if (!mapIds.size) {
        return [];
    }

    return getMatchesInRange(last25MatchData, maxMatches)
        .filter((row) => row?.map && mapIds.has(row.map))
        .map((row, index) => {
            const adr = Number(row.adr);
            const kills = Number(row.kills) || 0;
            const headshots = Number(
                row.headshot_kills ?? row.headshotKills ?? row.headshots,
            );
            const kast = extractMatchKastPercent(row);
            const hsPercent =
                kills > 0 && Number.isFinite(headshots)
                    ? Math.round((headshots / kills) * 1000) / 10
                    : null;

            return {
                match: index + 1,
                adr: Number.isFinite(adr) ? Math.round(adr * 10) / 10 : null,
                kast,
                hsPercent,
                timestamp: Number(row.timestamp) || 0,
            };
        });
}

export function getMapWinTrendDirection(results = []) {
    if (!results.length) {
        return "neutral";
    }

    const wins = results.filter((result) => result === "W").length;
    if (wins > results.length / 2) {
        return "up";
    }
    if (wins < results.length / 2) {
        return "down";
    }
    return "neutral";
}

const ACTIVE_DUTY_MAP_POOL_SIZE = 7;

export function getSortedMapStatEntries(mapsStats, limit = null) {
    const entries = Object.entries(mapsStats || {})
        .filter(([, stats]) => stats?.gamesPlayed > 0)
        .sort(([, a], [, b]) => {
            const byPlays = b.gamesPlayed - a.gamesPlayed;
            return byPlays !== 0 ? byPlays : (b.winRate || 0) - (a.winRate || 0);
        });

    return limit == null ? entries : entries.slice(0, limit);
}

function normalizeRatingScore(rating) {
    if (!Number.isFinite(rating)) {
        return 0;
    }
    return Math.max(0, Math.min(100, Math.round(((rating - 0.5) / 1) * 100)));
}

function normalizeAdrScore(adr) {
    if (!Number.isFinite(adr)) {
        return 0;
    }
    return Math.max(0, Math.min(100, Math.round(((adr - 50) / 60) * 100)));
}

export function buildMapPlayRadarProfile(mapsStats, last25MatchData, limit = null) {
    const entries = getSortedMapStatEntries(mapsStats, limit);
    if (!entries.length) {
        return null;
    }

    const mapRatings = buildMapRatingAverages(last25MatchData);
    let totalGames = 0;
    let totalWins = 0;
    let weightedAdr = 0;
    let ratingSum = 0;
    let ratingWeight = 0;
    let recentWins = 0;
    let recentGames = 0;
    const winRates = [];

    for (const [mapId, stats] of entries) {
        const gamesPlayed = stats.gamesPlayed;
        const gamesWon =
            stats.gamesWon ??
            Math.round(((stats.winRate ?? 0) / 100) * gamesPlayed);

        totalGames += gamesPlayed;
        totalWins += gamesWon;
        weightedAdr += (stats.avgAdr ?? 0) * gamesPlayed;
        winRates.push(stats.winRate ?? 0);

        if (Number.isFinite(mapRatings[mapId])) {
            ratingSum += mapRatings[mapId] * gamesPlayed;
            ratingWeight += gamesPlayed;
        }

        for (const result of stats.resultLastFiveGames || []) {
            recentGames += 1;
            if (result === "W") {
                recentWins += 1;
            }
        }
    }

    const winRate =
        totalGames > 0 ? Math.round((totalWins / totalGames) * 100) : 0;
    const avgAdr =
        totalGames > 0 ? Math.round((weightedAdr / totalGames) * 10) / 10 : null;
    const avgRating =
        ratingWeight > 0
            ? Math.round((ratingSum / ratingWeight) * 100) / 100
            : null;
    const mapPoolScore = Math.min(
        100,
        Math.round((entries.length / ACTIVE_DUTY_MAP_POOL_SIZE) * 100),
    );
    const consistencyScore =
        winRates.length >= 2
            ? Math.max(0, 100 - (Math.max(...winRates) - Math.min(...winRates)))
            : 75;
    const momentumScore =
        recentGames > 0
            ? Math.round((recentWins / recentGames) * 100)
            : winRate;

    return {
        summary: {
            winRate,
            avgRating,
            avgAdr,
            mapCount: entries.length,
            matchCount: totalGames,
        },
        axes: [
            {
                subject: "Win Rate",
                value: winRate,
                display: `${winRate}%`,
            },
            {
                subject: "Rating",
                value: normalizeRatingScore(avgRating),
                display: Number.isFinite(avgRating) ? avgRating.toFixed(2) : "—",
            },
            {
                subject: "ADR",
                value: normalizeAdrScore(avgAdr),
                display: Number.isFinite(avgAdr) ? String(avgAdr) : "—",
            },
            {
                subject: "Pool",
                value: mapPoolScore,
                display: `${entries.length} maps`,
            },
            {
                subject: "Steady",
                value: consistencyScore,
                display: `${consistencyScore}%`,
            },
            {
                subject: "Form",
                value: momentumScore,
                display: `${momentumScore}%`,
            },
        ],
    };
}
